import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';

import { Observable, Subscription } from 'rxjs';

import { unsubscribe } from '@fcom/core/utils';
import { ElementActions, ElementTypes, GaContext } from '@fcom/common/interfaces';
import { GtmService } from '@fcom/common/gtm';

import { FlightWithStreams } from '../../../interfaces';

@Component({
  selector: 'fin-seat-map-mini-map',
  templateUrl: './seat-map-mini-map.component.html',
  styleUrls: ['./seat-map-mini-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class SeatMapMiniMapComponent implements OnDestroy, OnChanges, AfterViewInit {
  @Input() flight: FlightWithStreams;
  @Input() viewPortWidth$: Observable<number | undefined>;
  @Input() scrollPosition: number;

  @Output() scrollTo: EventEmitter<number> = new EventEmitter();
  @Output() keyScrollDirection: EventEmitter<'left' | 'right'> = new EventEmitter();

  @ViewChild('selectedArea') selectedArea: ElementRef;
  @ViewChild('seatMapContainer') seatMapContainer: ElementRef;
  @ViewChild('wrapper') wrapper: ElementRef;

  svgWidth: number;
  subscription = new Subscription();

  constructor(private gtmService: GtmService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.scrollPosition) {
      this.setNewAreaPosition();
    }
  }

  ngAfterViewInit(): void {
    this.subscription.add(
      this.viewPortWidth$.subscribe((viewPortWidth) => {
        if (!viewPortWidth) {
          return;
        }
        const container = this.seatMapContainer.nativeElement;
        this.svgWidth = container.querySelector('svg')?.getAttribute('data-optimal-width');
        if (this.svgWidth) {
          const visibleAreaPercentage = viewPortWidth / this.svgWidth;
          this.selectedArea.nativeElement.style.width = `${container.offsetWidth * visibleAreaPercentage}px`;
        }
      })
    );

    this.setNewAreaPosition();
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
  }

  handleClick(e: MouseEvent): void {
    const parentWidth = this.seatMapContainer.nativeElement.offsetWidth;
    const parentLeft = this.seatMapContainer.nativeElement.getBoundingClientRect().left;
    const areaWidth = this.selectedArea.nativeElement.offsetWidth;

    const newPosition = e.clientX - parentLeft - areaWidth / 2;
    this.setSelectedAreaPosition(newPosition);

    this.scrollTo.emit((Math.max(0, newPosition) / parentWidth) * this.svgWidth);
    this.gtmService.trackElement('seatmap-mini', GaContext.SEAT_MAP, ElementTypes.BUTTON, '', ElementActions.CLICK);
  }

  setSelectedAreaPosition(newPosition: number): void {
    const parentWidth = this.seatMapContainer.nativeElement.offsetWidth;
    const areaWidth = this.selectedArea.nativeElement.offsetWidth;

    const parentBorder = (this.wrapper.nativeElement.offsetWidth - this.wrapper.nativeElement.clientWidth) / 2;
    const totalBorderWidth = this.getTotalBorderWidth();
    const minPosition = totalBorderWidth * -1;

    if (newPosition < minPosition) {
      newPosition = minPosition;
    }
    if (newPosition + areaWidth > parentWidth + totalBorderWidth) {
      newPosition = parentWidth - areaWidth + (totalBorderWidth / 2 + parentBorder);
    }

    requestAnimationFrame(() => {
      this.selectedArea.nativeElement.style.left = `${newPosition}px`;
    });
  }

  getTotalBorderWidth(): number {
    if (!this.selectedArea) {
      return 0;
    }

    const selectedAreaBorder =
      (this.selectedArea.nativeElement.offsetWidth - this.selectedArea.nativeElement.clientWidth) / 2;
    const parentBorder = (this.wrapper.nativeElement.offsetWidth - this.wrapper.nativeElement.clientWidth) / 2;

    return selectedAreaBorder + parentBorder;
  }

  setNewAreaPosition(): void {
    const borderWidth = this.getTotalBorderWidth();

    requestAnimationFrame(() => {
      let newPosition = (this.scrollPosition / this.svgWidth) * this.seatMapContainer.nativeElement.offsetWidth;

      if (newPosition === 0) {
        newPosition = -borderWidth;
      }

      this.setSelectedAreaPosition(newPosition);
    });
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'ArrowRight') {
      this.keyScrollDirection.emit('left');
    } else if (event.key === 'ArrowLeft') {
      this.keyScrollDirection.emit('right');
    }
  }

  clickFront(): void {
    this.scrollTo.emit(0);
  }

  clickBack(): void {
    this.scrollTo.emit(this.svgWidth);
  }
}
